import React from 'react';
import { Leaf, Zap, Droplets, ThermometerSun, TrendingDown } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SustainabilityMetrics: React.FC = () => {
  // Mock data - in real app this would come from sensors and building systems
  const sustainabilityData = {
    currentMetrics: {
      energySaved: 32.5,
      carbonReduced: 28.4,
      waterSaved: 15.2,
      optimizationScore: 89
    },
    hourlyEnergy: [
      { time: '8AM', actual: 45, baseline: 65 },
      { time: '9AM', actual: 52, baseline: 68 },
      { time: '10AM', actual: 58, baseline: 72 },
      { time: '11AM', actual: 62, baseline: 75 },
      { time: '12PM', actual: 65, baseline: 78 },
      { time: '1PM', actual: 63, baseline: 76 },
      { time: '2PM', actual: 60, baseline: 74 },
      { time: '3PM', actual: 58, baseline: 73 },
      { time: '4PM', actual: 55, baseline: 70 },
      { time: '5PM', actual: 50, baseline: 68 }
    ],
    smartAdjustments: [
      { time: '10:15 AM', action: 'Reduced HVAC in low-traffic zones', impact: '-12.5 kWh' },
      { time: '11:30 AM', action: 'Optimized lighting based on occupancy', impact: '-8.3 kWh' },
      { time: '2:45 PM', action: 'Adjusted ventilation rates', impact: '-15.2 kWh' }
    ],
    occupancyEfficiency: [
      { hour: '8-10', occupancy: 45, energyPerPerson: 2.8 },
      { hour: '10-12', occupancy: 75, energyPerPerson: 1.9 },
      { hour: '12-2', occupancy: 85, energyPerPerson: 1.7 },
      { hour: '2-4', occupancy: 70, energyPerPerson: 2.1 },
      { hour: '4-6', occupancy: 40, energyPerPerson: 2.9 }
    ]
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Sustainability Impact</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Zap className="text-yellow-500 mr-2" />
            <h3 className="text-lg font-semibold">Energy Saved</h3>
          </div>
          <p className="text-3xl font-bold">{sustainabilityData.currentMetrics.energySaved}%</p>
          <p className="text-gray-500">vs. baseline</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Leaf className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Carbon Reduced</h3>
          </div>
          <p className="text-3xl font-bold">{sustainabilityData.currentMetrics.carbonReduced}%</p>
          <p className="text-gray-500">carbon footprint</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Droplets className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Water Saved</h3>
          </div>
          <p className="text-3xl font-bold">{sustainabilityData.currentMetrics.waterSaved}%</p>
          <p className="text-gray-500">consumption reduced</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <ThermometerSun className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Optimization</h3>
          </div>
          <p className="text-3xl font-bold">{sustainabilityData.currentMetrics.optimizationScore}</p>
          <p className="text-gray-500">efficiency score</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Energy Usage vs. Baseline</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={sustainabilityData.hourlyEnergy}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="baseline" 
                stroke="#ff7300" 
                fill="#fff7e6" 
                name="Baseline Usage"
              />
              <Area 
                type="monotone" 
                dataKey="actual" 
                stroke="#82ca9d" 
                fill="#e6fff2" 
                name="Actual Usage"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Occupancy vs. Energy Efficiency</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sustainabilityData.occupancyEfficiency}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="occupancy" 
                stroke="#8884d8" 
                name="Occupancy %"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="energyPerPerson" 
                stroke="#82ca9d" 
                name="kWh per Person"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Smart Adjustments</h3>
          <TrendingDown className="text-green-500" />
        </div>
        <div className="space-y-4">
          {sustainabilityData.smartAdjustments.map((adjustment, index) => (
            <div key={index} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">{adjustment.time}</p>
                <p className="font-medium">{adjustment.action}</p>
              </div>
              <div className="text-green-500 font-semibold">{adjustment.impact}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SustainabilityMetrics; 